import { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InputBase from '@mui/material/InputBase';
import Tooltip from '@mui/material/Tooltip';
import { QRCodeSVG } from 'qrcode.react';

import { serialItems, customerAssignedLocations, fulfillmentTokens } from '../../services/dataClient';
import { useAuth } from '../../auth';
import '../../styles/design-tokens.css';

const TOKEN_TTL_MS = 30 * 60 * 1000; // 30 minutes

// ── Step indicator ─────────────────────────────────────────────────────────────
function StepIndicator({ current }) {
  const steps = ['Location', 'Serial', 'Confirm'];
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', mb: 2.5 }}>
      {steps.map((label, i) => {
        const n = i + 1;
        const active = n === current;
        const done = n < current;
        return (
          <Box key={label} sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
              <Box sx={{ width: 28, height: 28, borderRadius: '50%',
                backgroundColor: done || active ? '#009688' : 'var(--grey-200)',
                color: done || active ? '#fff' : 'var(--grey-500)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8125rem', fontWeight: 700, flexShrink: 0,
              }}>
                {done ? '✓' : n}
              </Box>
              <Typography sx={{ fontSize: '0.6875rem',
                color: active ? '#009688' : done ? 'var(--grey-600)' : 'var(--grey-400)',
                fontWeight: active ? 600 : 400,
                whiteSpace: 'nowrap',
              }}>
                {label}
              </Typography>
            </Box>
            {i < steps.length - 1 && (
              <Box sx={{ width: 56, height: 2,
                backgroundColor: done ? '#009688' : 'var(--grey-200)',
                mx: 1, mt: '13px',
              }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ── Main modal ─────────────────────────────────────────────────────────────────
export default function PortalSelfFulfillModal({ open, order, onClose, onFulfilled }) {
  const { currentUser } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedSerial, setSelectedSerial]   = useState(null);
  const [token, setToken]                       = useState('');
  const [verifyUrl, setVerifyUrl]               = useState('');
  const [pin, setPin]                           = useState('');
  const [photoReceived, setPhotoReceived]       = useState(false);
  const [copied, setCopied]                     = useState(false);
  const [tokenExpired, setTokenExpired]         = useState(false);
  const [timeLeft, setTimeLeft]                 = useState(TOKEN_TTL_MS);
  const pollRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedLocation(null);
      setSelectedSerial(null);
      setToken('');
      setVerifyUrl('');
      setPin('');
      setPhotoReceived(false);
      setTokenExpired(false);
      setCopied(false);
      setTimeLeft(TOKEN_TTL_MS);
    }
  }, [open]);

  // Generate token + PIN when entering step 3
  useEffect(() => {
    if (step !== 3 || !order) return;
    const tok = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    const p   = String(Math.floor(1000 + Math.random() * 9000));
    const data = {
      orderId:         order.id,
      ticketNumber:    order.ticketNumber,
      partNumber:      order.partNumber,
      partDescription: order.partDescription,
      locationName:    selectedLocation?.locationName ?? '',
      serialNumber:    selectedSerial?.serialNumber   ?? '',
      pin:             p,
      photoDataUrl:    null,
      uploadedAt:      null,
      createdAt:       Date.now(),
    };
    fulfillmentTokens.set(tok, data);
    // Persist to localStorage for same-browser polling
    localStorage.setItem(`coverit:ft:${tok}`, JSON.stringify(data));
    // Encode full payload into the URL so any device can verify without localStorage
    const payload = encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify(data)))));
    const url = `${window.location.origin}/portal/verify/${tok}?d=${payload}`;
    setToken(tok);
    setVerifyUrl(url);
    setPin(p);
    setPhotoReceived(false);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll localStorage every 1.5 s — works even when verify page is in a different tab.
  // Also tracks time remaining and marks the token expired after 30 minutes.
  useEffect(() => {
    if (step !== 3 || !token || photoReceived) return;
    pollRef.current = setInterval(() => {
      try {
        const raw = localStorage.getItem(`coverit:ft:${token}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          const age = Date.now() - (parsed.createdAt ?? Date.now());
          const remaining = TOKEN_TTL_MS - age;
          setTimeLeft(Math.max(0, remaining));
          if (remaining <= 0) {
            // Token expired — clean up and surface the error in the modal
            localStorage.removeItem(`coverit:ft:${token}`);
            fulfillmentTokens.delete(token);
            setTokenExpired(true);
            clearInterval(pollRef.current);
            return;
          }
          if (parsed.photoDataUrl) {
            fulfillmentTokens.set(token, parsed); // sync back to Map
            setPhotoReceived(true);
            clearInterval(pollRef.current);
          }
        }
      } catch { /* ignore parse errors */ }
    }, 1500);
    return () => clearInterval(pollRef.current);
  }, [step, token, photoReceived]);

  // On-site locations assigned to this customer that have available serials for the part
  const availableLocations = (() => {
    if (!order) return [];
    return customerAssignedLocations
      .filter(
        (cal) =>
          cal.customerName === currentUser.customerName &&
          cal.locationName.startsWith('On-Site —'),
      )
      .map((cal) => ({
        locationName:   cal.locationName,
        availableCount: serialItems.filter(
          (s) =>
            s.partNumber  === order.partNumber &&
            s.locationName === cal.locationName &&
            s.status       === 'Available',
        ).length,
      }))
      .filter((l) => l.availableCount > 0);
  })();

  // Available serials at the chosen location
  const availableSerials = selectedLocation
    ? serialItems.filter(
        (s) =>
          s.partNumber   === order?.partNumber &&
          s.locationName === selectedLocation.locationName &&
          s.status       === 'Available',
      )
    : [];

  const handleDone = () => {
    // Read final entry — prefer localStorage (may have been written by verify page in another tab)
    let entry = null;
    try {
      const raw = localStorage.getItem(`coverit:ft:${token}`);
      if (raw) entry = JSON.parse(raw);
    } catch { /* ignore */ }
    entry = entry ?? fulfillmentTokens.get(token);

    // Mutate the order object directly (module-level array mutation pattern)
    Object.assign(order, {
      status:            'Fulfilled',
      fulfillmentMethod: 'OnSite',
      locationName:      selectedLocation.locationName,
      serialNumber:      selectedSerial.serialNumber,
      fulfillmentPhoto:  {
        dataUrl:    entry?.photoDataUrl ?? null,
        uploadedAt: entry?.uploadedAt  ?? null,
        token,
      },
    });
    // Mutate the serial item
    const si = serialItems.find((s) => s.serialNumber === selectedSerial.serialNumber);
    if (si) {
      si.status        = 'Reserved';
      si.ticketNumber  = order.ticketNumber;
    }
    // Clean up token storage
    localStorage.removeItem(`coverit:ft:${token}`);
    fulfillmentTokens.delete(token);
    onFulfilled(order);
    onClose();
  };

  const btnSx = {
    backgroundColor: '#009688',
    '&:hover': { backgroundColor: '#00796B' },
    '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    boxShadow: 'none',
    px: 2,
  };

  const cancelSx = {
    fontSize: '0.875rem',
    color: 'var(--grey-500)',
    cursor: 'pointer',
    '&:hover': { color: 'var(--grey-700)' },
    mr: 'auto',
  };

  return (
    <Dialog
      open={open}
      onClose={(_, r) => { if (r !== 'backdropClick') onClose(); }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)',
        pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        Self Fulfill — {order?.ticketNumber}
        <IconButton
          size="small" onClick={onClose}
          sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}
        >
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '12px !important' }}>
        {order && (
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-600)', mb: 2 }}>
            {order.partNumber} — {order.partDescription}
          </Typography>
        )}

        <StepIndicator current={step} />
        <Divider sx={{ mb: 2.5 }} />

        {/* ── Step 1: Pick Location ─────────────────────────────────────────── */}
        {step === 1 && (
          <>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-600)', mb: 1.5 }}>
              Select a stock location to fulfill from:
            </Typography>
            {availableLocations.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-500)' }}>
                  No serialized stock available at your assigned on-site locations for this part.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {availableLocations.map((loc) => {
                  const isSelected = selectedLocation?.locationName === loc.locationName;
                  return (
                    <Box
                      key={loc.locationName}
                      onClick={() => setSelectedLocation(loc)}
                      sx={{ p: 2, borderRadius: '8px',
                        border: `2px solid ${isSelected ? '#009688' : 'var(--grey-200)'}`,
                        backgroundColor: isSelected ? '#E0F2F1' : 'var(--paper)',
                        cursor: 'pointer',
                        transition: 'border-color 0.15s, background-color 0.15s',
                        '&:hover': { borderColor: isSelected ? '#009688' : 'var(--grey-400)' },
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)' }}>
                          {loc.locationName}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
                          {loc.availableCount} unit{loc.availableCount !== 1 ? 's' : ''} available
                        </Typography>
                      </Box>
                      {isSelected && <CheckCircleIcon sx={{ fontSize: 20, color: '#009688', flexShrink: 0 }} />}
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}

        {/* ── Step 2: Pick Serial ───────────────────────────────────────────── */}
        {step === 2 && (
          <>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-600)', mb: 1.5 }}>
              Select a specific unit from{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'var(--grey-800)' }}>
                {selectedLocation?.locationName}
              </Box>
              :
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {availableSerials.map((s) => {
                const isSelected = selectedSerial?.serialNumber === s.serialNumber;
                return (
                  <Box
                    key={s.serialNumber}
                    onClick={() => setSelectedSerial(s)}
                    sx={{ px: 2, py: 1.5, borderRadius: '8px',
                      border: `2px solid ${isSelected ? '#009688' : 'var(--grey-200)'}`,
                      backgroundColor: isSelected ? '#E0F2F1' : 'var(--paper)',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s, background-color 0.15s',
                      '&:hover': { borderColor: isSelected ? '#009688' : 'var(--grey-400)' },
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 600,
                        color: 'var(--grey-900)', fontFamily: 'monospace', letterSpacing: '0.04em',
                      }}>
                        {s.serialNumber}
                      </Typography>
                      <Box sx={{ px: 1, py: '2px', borderRadius: '4px', backgroundColor: '#E8F5E9' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#388E3C' }}>
                          Available
                        </Typography>
                      </Box>
                    </Box>
                    {isSelected && <CheckCircleIcon sx={{ fontSize: 20, color: '#009688', flexShrink: 0 }} />}
                  </Box>
                );
              })}
            </Box>
          </>
        )}

        {/* ── Step 3: QR Code ───────────────────────────────────────────────── */}
        {step === 3 && token && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, py: 1 }}>
            {/* QR code */}
            <Box sx={{ p: 2.5, borderRadius: '12px',
              border: '1px solid var(--grey-200)',
              backgroundColor: '#fff',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}>
              <QRCodeSVG value={verifyUrl} size={180} level="M" includeMargin={false} />
            </Box>

            {/* URL hint */}
            <Box sx={{ width: '100%', maxWidth: 380, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-600)', mb: 1 }}>
                Scan with your phone to confirm the part
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center',
                border: '1px solid var(--grey-200)',
                borderRadius: '8px',
                backgroundColor: 'var(--grey-50)',
                overflow: 'hidden',
              }}>
                <InputBase
                  value={verifyUrl}
                  readOnly
                  inputProps={{ style: { fontSize: '0.7rem', color: 'var(--grey-500)', cursor: 'text' } }}
                  sx={{ flex: 1, px: 1.5, py: '6px', '& input': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                />
                <Tooltip title={copied ? 'Copied!' : 'Copy link'} placement="top" arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(verifyUrl).catch(() => {});
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    sx={{ borderLeft: '1px solid var(--grey-200)',
                      borderRadius: 0,
                      px: 1.25, py: '6px',
                      color: copied ? '#009688' : 'var(--grey-400)',
                      '&:hover': { backgroundColor: 'var(--grey-100)', color: '#009688' },
                    }}
                  >
                    {copied
                      ? <CheckCircleIcon sx={{ fontSize: 16 }} />
                      : <ContentCopyIcon sx={{ fontSize: 16 }} />
                    }
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* PIN display */}
            <Box sx={{ px: 4, py: 2, borderRadius: '10px',
              backgroundColor: '#F3F4FF',
              border: '1px solid #C5CAE9',
              textAlign: 'center',
            }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#5C6BC0', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5,
              }}>
                Confirmation Code
              </Typography>
              <Typography sx={{ fontSize: '2.25rem', fontWeight: 800,
                letterSpacing: '0.3em', color: '#3949AB',
                fontFamily: '"Roboto Mono", monospace',
              }}>
                {pin}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#7986CB', mt: 0.5 }}>
                Enter this code on your phone to verify
              </Typography>
            </Box>

            {/* Upload status */}
            {photoReceived ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 20, color: '#388E3C' }} />
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#388E3C' }}>
                  Photo received!
                </Typography>
              </Box>
            ) : tokenExpired ? (
              <Box sx={{ textAlign: 'center', px: 2 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#C62828', mb: 0.5 }}>
                  Link expired
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)' }}>
                  Go back and regenerate a new QR code.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%',
                    backgroundColor: '#FF9800', flexShrink: 0,
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%':      { opacity: 0.25 },
                    },
                    animation: 'pulse 1.6s ease-in-out infinite',
                  }} />
                  <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-500)' }}>
                    Waiting for photo upload…
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)' }}>
                  {(() => {
                    const totalSecs = Math.floor(timeLeft / 1000);
                    const m = Math.floor(totalSecs / 60);
                    const s = totalSecs % 60;
                    return `Expires in ${m}:${String(s).padStart(2, '0')}`;
                  })()}
                </Typography>
                <Typography
                  onClick={handleDone}
                  sx={{ fontSize: '0.75rem', color: 'var(--grey-400)', cursor: 'pointer', mt: 0.5,
                    '&:hover': { color: '#009688', textDecoration: 'underline' },
                  }}
                >
                  Complete without photo
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        {/* Step 1 actions */}
        {step === 1 && (
          <>
            <Typography onClick={onClose} sx={cancelSx}>Cancel</Typography>
            <Button
              variant="contained" size="small"
              disabled={!selectedLocation || availableLocations.length === 0}
              onClick={() => setStep(2)}
              sx={btnSx}
            >
              Next — Select Serial
            </Button>
          </>
        )}

        {/* Step 2 actions */}
        {step === 2 && (
          <>
            <Typography
              onClick={() => { setSelectedSerial(null); setStep(1); }}
              sx={cancelSx}
            >
              ← Back
            </Typography>
            <Button
              variant="contained" size="small"
              disabled={!selectedSerial}
              onClick={() => setStep(3)}
              sx={btnSx}
            >
              Next — Generate QR Code
            </Button>
          </>
        )}

        {/* Step 3 actions */}
        {step === 3 && (
          <>
            <Typography
              onClick={() => { setSelectedSerial(null); setStep(2); }}
              sx={cancelSx}
            >
              ← Back
            </Typography>
            <Button
              variant="contained" size="small"
              disabled={!photoReceived || tokenExpired}
              onClick={handleDone}
              sx={btnSx}
            >
              Done — Mark as Fulfilled
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
